import React from 'react';
import styled from 'styled-components';
import { animateScroll, scroller } from 'react-scroll';
import {
  Button,
  TinyTitleBold,
  Caption,
  SellIcon,
  KvdTestIcon,
  SearchIcon,
  mq
} from '@kvdbil/components';
import { useTranslation } from '~/Locale';
import { Media as IMedia } from '~/App/shared/interfaces/ProcessObject';
import { useIsDevice } from '~/App/shared/hooks/useIsDevice';

import Media from '~/App/shared/components/Media';

const Wrapper = styled.nav`
  background: ${({ theme }) => theme.colors.background.light};
  display: flex;
  align-items: center;
  justify-content: start;
  gap: 0.5rem;
  box-shadow: ${({ theme }) => theme.elevations.elevation4};
  overflow: hidden;
`;

const VehicleInformationWrapper = styled.div`
  display: flex;
  flex-direction: column;
  min-width: 300px;
  width: 100%;

  ${mq(null, 'tablet')} {
    display: none;
  }

  & > p {
    text-wrap: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }
`;

const ButtonWrapper = styled.div`
  display: flex;
  flex-direction: row;
  min-height: 4rem;
  width: 100%;
  justify-content: flex-end;

  ${mq(null, 'tablet')} {
    & > button {
      width: 100%;
    }
  }

  & > button {
    border-left: 1px solid ${({ theme }) => theme.colors.grayscaleToned.light5};
    justify-content: center;
    gap: 0.5rem;

    & > span {
      margin-right: 0;
    }
  }

  ${mq('laptop')} {
    & > button {
      width: 8.95rem;
    }
  }
`;

const ImageWrapper = styled.div`
  width: 14rem;

  ${mq(null, 'tablet')} {
    display: none;
  }
`;

interface Props {
  picture?: IMedia;
  title: string;
  carPackage: string;
  isHeavyEquipment?: boolean;
}

const StickyAuctionSummary = ({
  picture,
  title,
  carPackage,
  isHeavyEquipment = false
}: Props) => {
  const { t } = useTranslation();
  const { isMobile } = useIsDevice();

  const buttons = [
    {
      text: t('Buy'),
      icon: <SellIcon />,
      anchor: 'buyMethodAnchor'
    },
    {
      text: t('Test'),
      icon: <KvdTestIcon />,
      anchor: isHeavyEquipment ? '' : 'testAnchor'
    },
    {
      text: t('Info'),
      icon: <SearchIcon />,
      anchor: 'infoAnchor'
    }
  ];

  return (
    <Wrapper aria-label={t('Sticky navigation')}>
      <ImageWrapper>
        <Media
          src={picture?.uri || ''}
          onClick={() => animateScroll.scrollToTop()}
          width={60}
          height={37.5}
          quality={30}
          imageType="THUMBNAIL"
        />
      </ImageWrapper>
      <VehicleInformationWrapper>
        <TinyTitleBold>{title}</TinyTitleBold>
        <Caption>{carPackage}</Caption>
      </VehicleInformationWrapper>
      <ButtonWrapper>
        {buttons
          .filter(button => Boolean(button?.anchor))
          .map(({ text, icon, anchor }, index) => (
            <Button
              key={index}
              prefixIcon={icon}
              variant="flat"
              color="neutral"
              size="regular"
              type="button"
              noRadius
              aria-label={t('Go to %s section', text)}
              onClick={() =>
                scroller.scrollTo(anchor, {
                  duration: 750,
                  smooth: true,
                  dynamic: true,
                  offset: isMobile ? 0 : -65
                })
              }
            >
              {text}
            </Button>
          ))}
      </ButtonWrapper>
    </Wrapper>
  );
};

export default StickyAuctionSummary;
